import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';

// Explicit request (EnquiryFormDrawer split): renders `children(pickedValues)` and re-runs it only when one of
// the values `pick` returns changes (compared one level deep), or when `children` itself is a new function.
//
// react-final-form's own <Form subscription={{ values: true }}> re-renders the whole form body on every
// keystroke. With this component the body is subscribed to just the handful of values its own markup reads,
// and each piece that needs the complete values subscribes to them locally (FormSpy) or reads
// form.getState().values when an event fires.
interface PickedFormValuesProps<Picked extends object> {
    pick: (values: AnyObject) => Picked;
    children: (pickedValues: Picked) => React.ReactNode;
}

interface PickedFormValuesBodyProps {
    pickedValues: object;
    renderChildren: (pickedValues: any) => React.ReactNode; // tslint:disable-line: no-any
}

const isShallowEqual = (previous: object, next: object): boolean => {
    const previousKeys = Object.keys(previous);
    const nextKeys = Object.keys(next);
    if (previousKeys.length !== nextKeys.length) {
        return false;
    }
    return previousKeys.every((key) => (previous as Record<string, unknown>)[key] === (next as Record<string, unknown>)[key]);
};

const PickedFormValuesBody = React.memo(
    ({ pickedValues, renderChildren }: PickedFormValuesBodyProps) => <>{renderChildren(pickedValues)}</>,
    (previous, next) => previous.renderChildren === next.renderChildren && isShallowEqual(previous.pickedValues, next.pickedValues),
);
PickedFormValuesBody.displayName = 'PickedFormValuesBody';

export const PickedFormValues = <Picked extends object>({ pick, children }: PickedFormValuesProps<Picked>) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => <PickedFormValuesBody pickedValues={pick(values)} renderChildren={children} />}
    </FormSpy>
);
